import { t } from '@avalara/bat-apps-shared/i18n';
import fallbackTranslations from '../../../i18n/locales/en-US.json';
import { signInLinkAndText, submitPublicSupportCaseTextAndLink } from './constants';

export const emailExistsMessage = (productName: string, isFreeTrial?: boolean | undefined, signInUrl?: string) => {
  const signInLink = signInUrl ? `<a href="${signInUrl}">Sign in</a>` : signInLinkAndText;
  return isFreeTrial
    ? t('client.errors.email-exists-free-trial-message', { productName, signInLink })
    : t('client.errors.email-exists-purchase-message', { productName, signInLink });
};

export const getErrorMatcher = (key: keyof typeof fallbackTranslations) => fallbackTranslations[key];
export const matchesError = (resultMessage: string, matcherKey: keyof typeof fallbackTranslations, flags = '') =>
  new RegExp(getErrorMatcher(matcherKey), flags).test(resultMessage);

export const isInvalidAddressError = (resultMessage: string) =>
  matchesError(resultMessage, 'client.errors.matcher.invalid-address', 'i');

type FormatAccountSetupErrorProps = {
  isFreeTrial?: boolean;
  passwordRedirectUrl?: string;
  productName: string;
  redirectUrl: string;
  resultMessage: string;
  signInUrl?: string;
};

export const formatAccountSetupError = ({
  isFreeTrial,
  resultMessage,
  redirectUrl,
  productName,
  passwordRedirectUrl = '',
  signInUrl,
}: FormatAccountSetupErrorProps) => {
  let html = '';
  let message = '';
  let title = t('client.errors.generic-title');
  let locationAfterErrorDismissal = '';
  if (
    matchesError(resultMessage, 'client.errors.matcher.email-already-exists') ||
    matchesError(resultMessage, 'client.errors.matcher.user-already-exists')
  ) {
    html = emailExistsMessage(productName, isFreeTrial, signInUrl);
    title = t('client.errors.account-exists-title');
    locationAfterErrorDismissal = redirectUrl;
  } else if (matchesError(resultMessage, 'client.errors.matcher.account-already-exists', 'i')) {
    html = t('client.errors.account-exists-message', {
      signInLinkAndText,
      submitPublicSupportCaseTextAndLink,
    });
    title = t('client.errors.account-exists-title');
    locationAfterErrorDismissal = redirectUrl;
  } else if (isInvalidAddressError(resultMessage)) {
    message = t('client.errors.invalid-address-message');
    title = t('client.errors.invalid-address-title');
  } else if (matchesError(resultMessage, 'client.errors.matcher.common-password')) {
    message = t('client.errors.common-password-message');
    title = t('client.errors.common-password-title');
    locationAfterErrorDismissal = passwordRedirectUrl;
  } else if (matchesError(resultMessage, 'client.errors.matcher.password-length')) {
    message = t('client.errors.password-length-message');
    title = t('client.errors.password-length-title');
    locationAfterErrorDismissal = passwordRedirectUrl;
  } else if (matchesError(resultMessage, 'client.errors.matcher.password-complexity')) {
    message = t('client.errors.password-complexity-message');
    title = t('client.errors.password-complexity-title');
    locationAfterErrorDismissal = passwordRedirectUrl;
  } else if (matchesError(resultMessage, 'client.errors.matcher.esb-signup-key-invalid')) {
    message = t('client.errors.esb-signup-key-invalid-message');
    html = t('client.errors.keep-seeing-this-message', { submitPublicSupportCaseTextAndLink });
    locationAfterErrorDismissal = redirectUrl;
  } else if (matchesError(resultMessage, 'client.errors.matcher.signup-key-invalid')) {
    message = t('client.errors.signup-key-invalid-message');
    html = t('client.errors.keep-seeing-this-message', { submitPublicSupportCaseTextAndLink });
  }

  return { html, message, title, locationAfterErrorDismissal };
};
